// src/ReportPage.js
import React, { useState } from 'react';

const ReportPage = ({ inspection }) => {
  const colorBlue = '#153BC6';
  const colorGold = '#E5BB0B';

  // For enlarged modal view:
  const [selectedImage, setSelectedImage] = useState(null);
  const [zoomed, setZoomed] = useState(false);

  // Format the date
  const dateOnly = new Date(inspection.inspectionDateTime).toLocaleDateString();

  // Show check or X
  const renderCheckIcon = (isGood) =>
    isGood ? (
      <span style={{ color: 'green', fontWeight: 'bold', marginLeft: 5 }}>&#10003;</span>
    ) : (
      <span style={{ color: 'red', fontWeight: 'bold', marginLeft: 5 }}>&#10007;</span>
    );

  // Abbreviate inspector's last name
  const inspectorLastNameAbbrev = () => {
    const ln = inspection.inspector?.lastName || '';
    return ln ? ln.charAt(0) + '.' : '';
  };

  // Modal open / close
  function openImageModal(url) {
    setSelectedImage(url);
    setZoomed(false);
  }
  function closeModal() {
    setSelectedImage(null);
    setZoomed(false);
  }
  function toggleZoom() {
    setZoomed((prev) => !prev);
  }

  return (
    <div
      className="report-page"
      style={{
        width: '8.5in',
        minHeight: '11in',
        margin: '0 auto',
        padding: '0.3in',
        backgroundColor: '#ffffff',
        border: '1px solid #ccc',
        boxSizing: 'border-box',
        fontFamily: 'Arial, sans-serif',
      }}
    >
      {/* HEADER / LOGO */}
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: '0.2in' }}>
        <img src="/logo.png" alt="Company Logo" style={{ height: 50, marginRight: 15 }} />
      </div>

      {/* TITLE BAR */}
      <div
        style={{
          backgroundColor: colorBlue,
          color: 'white',
          padding: '8px 12px',
          marginBottom: '0.2in',
          fontSize: '1.1rem',
          fontWeight: 'bold',
        }}
      >
        Comprehensive Vehicle Condition Report
      </div>

      {/* TOP SECTION: Inspection # and Date */}
      <div style={{ marginBottom: '0.01in' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <tbody>
            <tr>
              <td style={{ padding: '4px 8px', width: '50%' }}>
                <strong>Inspection #:</strong> {inspection.inspectionId}
              </td>
              <td style={{ padding: '4px 8px', width: '50%' }}>
                <strong>Date:</strong> {dateOnly}
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* TWO-COLUMN MIDDLE SECTION */}
      <div style={{ display: 'flex', gap: '10px', marginBottom: '0.2in' }}>
        {/* LEFT COLUMN: Vehicle, Inspector, Customer Info & Critical Checks */}
        <div style={{ flex: 1 }}>
          <div style={{ marginBottom: '0.2in', borderLeft: `5px solid ${colorBlue}`, paddingLeft: 8 }}>
            <h3 style={{ color: colorBlue, marginBottom: 4, fontSize: '1rem' }}>Vehicle Information</h3>
            <p style={{ margin: '2px 0' }}>
              <strong>VIN:</strong> {inspection.vehicle?.vin}
            </p>
            <p style={{ margin: '2px 0' }}>
              <strong>Make:</strong> {inspection.vehicle?.make}{' '}
              <strong>Model:</strong> {inspection.vehicle?.model}{' '}
              <strong>Year:</strong> {inspection.vehicle?.modelYear}
            </p>
            <p style={{ margin: '2px 0' }}>
              <strong>Odometer:</strong> {inspection.odometer}
            </p>
          </div>

          <div style={{ marginBottom: '0.2in', borderLeft: `5px solid ${colorBlue}`, paddingLeft: 8 }}>
            <h3 style={{ color: colorBlue, marginBottom: 4, fontSize: '1rem' }}>Inspector Information</h3>
            <p style={{ margin: '2px 0' }}>
              <strong>Name:</strong> {inspection.inspector?.firstName} {inspectorLastNameAbbrev()}
            </p>
            <p style={{ margin: '2px 0' }}>
              <strong>Email:</strong> {inspection.inspector?.email}
            </p>
          </div>

          <div style={{ marginBottom: '0.2in', borderLeft: `5px solid ${colorBlue}`, paddingLeft: 8 }}>
            <h3 style={{ color: colorBlue, marginBottom: 4, fontSize: '1rem' }}>Customer Information</h3>
            <p style={{ margin: '2px 0' }}>
              <strong>Name:</strong> {inspection.customer?.firstName} {inspection.customer?.lastName}
            </p>
            <p style={{ margin: '2px 0' }}>
              <strong>Email:</strong> {inspection.customer?.email}
            </p>
          </div>

          {/* Critical Checks moved to left column under Customer Information */}
          <div style={{ marginBottom: '0.2in', borderLeft: `5px solid ${colorBlue}`, paddingLeft: 8 }}>
            <h3 style={{ color: colorBlue, marginBottom: 4, fontSize: '1rem' }}>Critical Checks</h3>
            <p style={{ margin: '2px 0' }}>
              <strong>Structural:</strong>{renderCheckIcon(!inspection.structuralDamage)}
            </p>
            <p style={{ margin: '2px 0' }}>
              <strong>Flood:</strong>{renderCheckIcon(!inspection.floodDamage)}
            </p>
            <p style={{ margin: '2px 0' }}>
              <strong>Hail:</strong>{renderCheckIcon(!inspection.hailDamage)}
            </p>
          </div>
        </div>

        {/* RIGHT COLUMN: RevCheck Summary */}
        <div style={{ flex: 1 }}>
          <div style={{ marginBottom: '0.2in', borderLeft: `5px solid ${colorBlue}`, paddingLeft: 8 }}>
            <h3 style={{ color: colorBlue, marginBottom: 4, fontSize: '1rem' }}>RevCheck Summary</h3>
            <p style={{ margin: '2px 0', lineHeight: 1.4 }}>
              {inspection.overallVehicleSummary}
            </p>
          </div>
        </div>
      </div>

      {/* PHOTO SECTION (Now enforced as a square thumbnail) */}
      {inspection.leftFrontCornerImageUrl && (
        <div style={{ marginBottom: '0.2in', borderLeft: `5px solid ${colorGold}`, paddingLeft: 8 }}>
          <h3 style={{ color: colorGold, marginBottom: 4, fontSize: '1rem' }}>Vehicle Photo</h3>
          <div
            style={{
              position: 'relative',
              width: '100%',
              paddingBottom: '100%', // 1:1 aspect ratio
              overflow: 'hidden',
              border: '2px solid #ccc',
              borderRadius: 4,
              cursor: 'pointer',
            }}
            onClick={() => openImageModal(inspection.leftFrontCornerImageUrl)}
          >
            <img
              src={inspection.leftFrontCornerImageUrl}
              alt="Left Front Corner"
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                objectFit: 'cover',
              }}
            />
          </div>
        </div>
      )}

      {/* FOOTER */}
      <div style={{ marginTop: '0.2in', fontSize: '0.8rem', color: '#666' }}>
        <hr style={{ border: 'none', borderTop: `1px solid ${colorBlue}` }} />
        <p style={{ marginTop: 4 }}>
          Thank you for choosing our Comprehensive Vehicle Condition Report.
        </p>
      </div>

      {/* IMAGE MODAL OVERLAY */}
      {selectedImage && (
        <div
          onClick={closeModal} // Click outside to close
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            backgroundColor: 'rgba(0,0,0,0.7)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
          }}
        >
          {/* The image itself toggles zoom on click, stopping event propagation */}
          <img
            src={selectedImage}
            alt="Enlarged"
            onClick={(e) => {
              e.stopPropagation();
              toggleZoom();
            }}
            style={{
              maxWidth: zoomed ? '95%' : '60%',
              maxHeight: zoomed ? '95%' : '60%',
              border: '2px solid #fff',
              borderRadius: 6,
              cursor: zoomed ? 'zoom-out' : 'zoom-in',
              transition: 'all 0.3s ease',
            }}
          />

          {/* The “X” close button in top-right corner */}
          <div
            onClick={(e) => {
              e.stopPropagation();
              closeModal();
            }}
            style={{
              position: 'absolute',
              top: 10,
              right: 20,
              color: 'white',
              fontSize: '2rem',
              fontWeight: 'bold',
              cursor: 'pointer',
            }}
          >
            &times;
          </div>
        </div>
      )}
    </div>
  );
};

export default ReportPage;
